import { feeModel, feeRoi, breakEvenPrice, type FeeInputs, type FeeLine, type FeeResult, type RatesCheck } from "./marketplaceModel";

/**
 * Marketplace fee presets and the side-by-side comparison (CAP-384-1). Pure, so the site and the API
 * (/v1/business/marketplace-fee, synced by scripts/sync-core.mjs) read the same rates and build the same lines.
 * Every number is a starting point and stays editable on the site.
 */
// "pay" is a separate payment processor charge; it is 0 where the marketplace fee already includes payment processing.
const OCT: RatesCheck = { month: "October 2026", official: false };
/** eBay: read from ebay.com's selling-fees page on 30 Oct 2026: 13.6% up to $7,500 and 2.35% above, charged on shipping and sales tax too; $0.30 or $0.40 per order. */
export const EBAY_CHECK: RatesCheck = { month: "October 2026", official: true, source: "ebay.com selling fees page, read 30 Oct 2026", on: "2026-10-30" };
export const EBAY_TIER = { above: 7500, pct: 2.35 };
/** Amazon: sell.amazon.com/pricing, read 1 Nov 2026: most categories 15% of price + shipping + gift wrap (some 8%, 20%, 45%; most have a $0.30 minimum); Individual plan $0.99 per item; Professional plan $39.99 a month. */
export const AMAZON_CHECK: RatesCheck = { month: "November 2026", official: true, source: "sell.amazon.com/pricing, read 1 Nov 2026", on: "2026-11-01" };
/**
 * Etsy (US shop, US bank account), read 1 Nov 2026: listing fee $0.20 per listing (help.etsy.com Fee Basics; etsy.com/legal/fees updated 5 Oct 2026);
 * transaction fee 6.5% of price + shipping + gift wrap; payment processing 3% + $0.25 on the order total including shipping and sales tax
 * (help.etsy.com payment processing article); Offsite Ads 15%, or 12% once the shop has $10,000+ of sales in 365 days, capped at $100 per order.
 */
export const ETSY_CHECK: RatesCheck = { month: "November 2026", official: true, source: "etsy.com fees policy and Etsy help pages, read 1 Nov 2026", on: "2026-11-01" };
export const ETSY_OFFSITE_CAP = 100;

export interface MarketplacePreset { fee: string; fixed: string; payPct: string; payFixed: string; note: string; check?: RatesCheck; fixedLabel?: string }
export const PRESETS: Record<string, MarketplacePreset> = {
  Amazon: { fee: "15", fixed: "0", payPct: "0", payFixed: "0", check: AMAZON_CHECK, note: "Referral fee varies by category (roughly 8–45%; 15% for most), on the item price plus shipping, usually with a $0.30 minimum. Payment processing is included. Amazon FBA sellers add a fulfilment fee below; individual-plan sellers also pay $0.99 per item (not included here)." },
  Etsy: { fee: "6.5", fixed: "0.20", payPct: "3", payFixed: "0.25", check: ETSY_CHECK, fixedLabel: "Listing fee", note: "6.5% transaction fee on item price plus shipping, a $0.20 listing fee (in per-order fixed fees) and 3% + $0.25 payment processing (US bank account; rates differ by country). Offsite Ads (15%, or 12% above $10,000 of sales in a year, capped at $100 per order) apply only to sales that came from an ad click and can be added in Compare." },
  eBay: { fee: "13.6", fixed: "0.40", payPct: "0", payFixed: "0", check: EBAY_CHECK, fixedLabel: "Per-order fee", note: "About 13.6% final value fee for most categories (some are lower or higher), on the item price plus shipping and sales tax, falling to 2.35% on the part of a sale above $7,500; plus $0.40 per order on orders over $10 ($0.30 on orders of $10 or less). Payment processing is included." },
  Walmart: { fee: "15", fixed: "0", payPct: "0", payFixed: "0", check: { month: "October 2026", official: true }, note: "About 15% referral fee for most categories (it varies by category, from roughly 6% to 15% or more), no per-order fee. Payment processing is included." },
  Mercari: { fee: "10", fixed: "0", payPct: "2.9", payFixed: "0.50", check: { month: "October 2026", official: true }, note: "10% selling fee plus about 2.9% + $0.50 payment processing." },
  "TikTok Shop": { fee: "6", fixed: "0.30", payPct: "0", payFixed: "0", check: { month: "October 2026", official: true }, fixedLabel: "Per-order fee", note: "Approximate: about 6% referral fee plus $0.30 per order. Fees vary by category and region and may be different for new sellers." },
  Shopify: { fee: "0", fixed: "0", payPct: "2.9", payFixed: "0.30", check: OCT, note: "Shopify takes no per-sale cut on paid plans; the cost is the card-processing fee (rate depends on your plan)." },
  Custom: { fee: "10", fixed: "0", payPct: "0", payFixed: "0", note: "Enter your own rates." },
};

/** Marketplaces that can be put side by side (Custom has no preset rates). */
export const COMPARE_MARKETS = Object.keys(PRESETS).filter((m) => m !== "Custom");
export const COMPARE_MIN = 2;
export const COMPARE_MAX = 4;

/** Inputs shared by every column of the comparison. */
export interface CompareShared {
  price: number; cost: number; shipCharged: number; shipCost: number; fulfil: number; cac: number;
  returnPct: number; returnShip: number; unsellable: number; feeOnShip: boolean;
  /** Sales tax collected on the order; counted in the fee base on eBay only. */
  salesTax: number;
  /** Etsy Offsite Ads percentage: 0 (off), 12 or 15. */
  offsitePct: number;
}

/** The fee inputs one marketplace column uses. Fulfilment (FBA) is charged to Amazon only; eBay's fixed fee is $0.30 on orders of $10 or less. */
export function presetInputs(market: string, s: CompareShared): FeeInputs {
  const p = PRESETS[market];
  const gross = s.price + s.shipCharged;
  return {
    price: s.price, cost: s.cost, shippingCharged: s.shipCharged, shippingCost: s.shipCost, feePct: Number(p.fee), feeOnShipping: s.feeOnShip,
    fixedFee: market === "eBay" && gross + Math.max(0, s.salesTax) <= 10 ? 0.3 : Number(p.fixed),
    paymentPct: Number(p.payPct), paymentFixed: Number(p.payFixed), fulfilment: market === "Amazon" ? s.fulfil : 0, cac: s.cac,
    returnPct: s.returnPct, returnShipping: s.returnShip, unsellablePct: s.unsellable,
    ...(market === "eBay" ? { salesTaxInFeeBase: Math.max(0, s.salesTax), feeTier: EBAY_TIER } : {}),
    ...(market === "Etsy" && s.offsitePct > 0 ? { offsiteAdsPct: s.offsitePct, offsiteAdsCap: ETSY_OFFSITE_CAP } : {}),
  };
}

const c2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** One column of the comparison: every fee on its own line, ending at the profit if the order is kept. The lines add up to that profit. */
export function compareLines(market: string, i: FeeInputs, r: FeeResult): FeeLine[] {
  const p = PRESETS[market];
  const plus = (label: string, n: number): FeeLine[] => (n !== 0 ? [{ label, amount: c2(n), kind: "plus" as const }] : []);
  const minus = (label: string, n: number): FeeLine[] => (n !== 0 ? [{ label, amount: -c2(n), kind: "minus" as const }] : []);
  const base = i.feeOnShipping ? "price + shipping" : "price";
  const gross = i.price + i.shippingCharged;
  const lines: FeeLine[] = [
    { label: "Selling price", amount: c2(i.price), kind: "plus" },
    ...plus("Shipping charged to the buyer", i.shippingCharged),
    ...minus("Your cost", i.cost),
    ...minus("Your shipping cost", i.shippingCost),
    ...minus(`Marketplace fee (${i.feePct}% of ${market === "eBay" && (i.salesTaxInFeeBase ?? 0) > 0 ? `${base} + sales tax` : base})`, r.marketplaceFee),
    ...minus(p.fixedLabel ? `${p.fixedLabel} (${c2(i.fixedFee)})` : "Per-order fixed fees", r.fixed),
    ...minus(`Payment processing percentage (${i.paymentPct}% of price + shipping)`, gross * i.paymentPct / 100),
    ...minus(`Payment processing fixed amount (${c2(i.paymentFixed)})`, i.paymentFixed),
    ...minus(`Etsy Offsite Ads fee (${i.offsiteAdsPct ?? 0}%, capped at ${ETSY_OFFSITE_CAP} per order)`, r.offsiteAds),
    ...minus("Fulfilment fee", i.fulfilment),
    ...minus("Ad / acquisition cost", i.cac),
  ];
  const sum = c2(lines.reduce((a, l) => a + l.amount, 0));
  const profit = c2(r.profitIfKept);
  const diff = c2(profit - sum);
  if (diff !== 0) lines.push({ label: "Rounding (each line is rounded to the cent)", amount: diff, kind: diff > 0 ? "plus" : "minus" });
  lines.push({ label: "Profit if the order is kept", amount: profit, kind: "total" });
  return lines;
}

export interface CompareColumn {
  market: string; check?: RatesCheck; inputs: FeeInputs; result: FeeResult; lines: FeeLine[];
  /** What the marketplace pays out: price + shipping charged less its own fees (marketplace, processing, per-order, Offsite Ads). */
  payout: number;
  roiPct: number | null; breakEvenPrice: number | null;
}

export function compareColumn(market: string, s: CompareShared): CompareColumn {
  const inputs = presetInputs(market, s);
  const result = feeModel(inputs);
  return {
    market, check: PRESETS[market].check, inputs, result, lines: compareLines(market, inputs, result),
    payout: c2(inputs.price + inputs.shippingCharged - result.marketplaceFee - result.paymentFee - result.fixed - result.offsiteAds),
    roiPct: feeRoi(inputs, result), breakEvenPrice: breakEvenPrice(inputs),
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// Shareable link: the inputs live in the URL query only (nothing is stored anywhere).
// ---------------------------------------------------------------------------------------------------------------------
export interface ShareState {
  compare: boolean; markets: string[]; market: string; currency: string;
  price: string; cost: string; shipCharged: string; shipCost: string; fulfil: string; cac: string; returnPct: string; returnShip: string; unsellable: string;
  feeOnShip: boolean; salesTax: string; offsite: string; orders: string;
  feePct: string; fixedFee: string; payPct: string; payFixed: string;
}
const NUM_KEYS: [keyof ShareState, string][] = [
  ["price", "p"], ["cost", "k"], ["shipCharged", "sc"], ["shipCost", "sk"], ["fulfil", "f"], ["cac", "a"], ["returnPct", "r"], ["returnShip", "rp"], ["unsellable", "u"],
  ["salesTax", "t"], ["orders", "v"], ["feePct", "fp"], ["fixedFee", "fx"], ["payPct", "pp"], ["payFixed", "pf"],
];
const NUM_RE = /^\d{0,9}(\.\d{0,4})?$/;

export function encodeShare(s: ShareState): string {
  const q = new URLSearchParams();
  if (s.compare) q.set("c", "1");
  q.set("m", s.compare ? s.markets.join(",") : s.market);
  if (s.currency !== "USD") q.set("cur", s.currency);
  for (const [k, key] of NUM_KEYS) { const v = String(s[k] ?? "").trim(); if (v !== "") q.set(key, v); }
  q.set("fs", s.feeOnShip ? "1" : "0");
  if (s.offsite !== "0") q.set("oa", s.offsite);
  return q.toString();
}

/** Reads a shared link. Anything that is not a plain number, a known marketplace or an allowed value is ignored. */
export function decodeShare(search: string): Partial<ShareState> {
  const q = new URLSearchParams(search);
  const out: Partial<ShareState> = {};
  const m = (q.get("m") ?? "").split(",").map((x) => x.trim()).filter((x) => x in PRESETS);
  if (q.get("c") === "1") {
    const markets = m.filter((x, k) => COMPARE_MARKETS.includes(x) && m.indexOf(x) === k).slice(0, COMPARE_MAX);
    if (markets.length >= COMPARE_MIN) { out.compare = true; out.markets = markets; }
  } else if (m.length === 1) out.market = m[0];
  const cur = q.get("cur");
  if (cur && /^[A-Z]{3}$/.test(cur)) out.currency = cur;
  for (const [k, key] of NUM_KEYS) { const v = q.get(key); if (v !== null && NUM_RE.test(v)) (out as Record<string, unknown>)[k] = v; }
  if (q.get("fs") === "0" || q.get("fs") === "1") out.feeOnShip = q.get("fs") === "1";
  const oa = q.get("oa");
  if (oa === "12" || oa === "15") out.offsite = oa;
  return out;
}
